import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ViewportScroller } from '@angular/common';
import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, withInMemoryScrolling, withPreloading, provideRouter } from '@angular/router';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore } from '@ngrx/router-store';
import { environment } from '@env/environment';
import { appReducer, clearState, routerStateConfig } from './core/ngrx';
import { AuthEffects } from './core/ngrx/effects/auth.effects';
import { KpiEffects } from './core/ngrx/effects/kpi.effects';
import { LayoutEffects } from './core/ngrx/effects/layout.effects';
import { OrdersEffects } from './core/ngrx/effects/orders.effects';
import { UserEffects } from './core/ngrx/effects/user.effects';
import { LocationsEffects } from './core/ngrx/effects/locations.effects';
import { EcpErrorHandler } from './core/services';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { ErrorHandlerInterceptor } from './core/interceptors/error-handler.interceptor';
import { CustomHammerConfig } from './core/config';
import { CustomMSALInterceptor } from './auth/auth.interceptor';
import { APP_ROUTES } from './app.routes';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.MSAL.clientId,
      authority: environment.MSAL.authority,
      redirectUri: environment.MSAL.redirectUri,
      postLogoutRedirectUri: environment.MSAL.logoutUri,
      knownAuthorities: environment.MSAL.knownAuthorities,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  const apiResourcePattern = `${environment.apiUrl}${environment.apiUrl.endsWith('/') ? '*' : '/*'}`;
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(apiResourcePattern, [...environment.MSAL.scopes]);
  protectedResourceMap.set('/api/*', [...environment.MSAL.scopes]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.MSAL.scopes],
    },
    loginFailedRoute: '/',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      APP_ROUTES,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideAppInitializer(() => {
      inject(ViewportScroller).setOffset([0, 70]);
    }),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideStore(appReducer, { metaReducers: [clearState] }),
    provideRouterStore(routerStateConfig),
    provideEffects(AuthEffects, KpiEffects, LayoutEffects, OrdersEffects, UserEffects, LocationsEffects),
    provideStoreDevtools({
      name: 'My Link App',
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    { provide: ErrorHandler, useClass: EcpErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorHandlerInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CustomMSALInterceptor, multi: true },
    { provide: HAMMER_GESTURE_CONFIG, useClass: CustomHammerConfig },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
};
